import type { ReactElement } from 'react';

import BpkText from '@skyscanner-internal/backpack-web/bpk-component-text';
import StringsPropVerification from '@web-platform/global-components-higher-order-components/StringsPropVerification';
import { fillPlaceholders } from '@web-platform/global-components-utils/stringUtils';

import type { Logger } from '@web-platform/global-components-types/utils';

import STYLES from './Copyright.module.scss';

const stringsKeys = ['footerCopyright'] as const;

/**
 * @interface
 * @public
 * @property footerCopyright - Copyright text for the footer.
 */
export type Strings = {
  [key in (typeof stringsKeys)[number]]: string;
};

/**
 * @public
 */
export interface CopyrightProps {
  /**
   *  A boolean determining if the environment is production.
   */
  isProductionEnv: boolean;
  /**
   * Logger object.
   */
  logger: Logger;
  /**
   * Translated strings for the component.
   */
  strings: Strings;
}

/**
 * The `Copyright` component displays copyright information.
 * @public
 *
 * @example
 * ```tsx
 * import Copyright from '@skyscanner-internal/global-components/copyright';
 *
 * const strings = {
 *   footerCopyright: '© Skyscanner Ltd 2002 – @@currentYear@@',
 * };
 *
 * const Layout = () => {
 *   return <Copyright
 *     isProductionEnv={true}
 *     logger={logger}
 *     strings={strings}
 *   />
 * }
 * ```
 */
const Copyright = ({ isProductionEnv = false, logger, strings }: CopyrightProps): ReactElement => {
  const { footerCopyright } = strings;
  const currentYear = new Date().getFullYear().toString();

  return (
    <StringsPropVerification
      stringsKeys={stringsKeys}
      componentName="Copyright"
      strings={strings}
      logger={logger}
      isProductionEnv={isProductionEnv}
    >
      <div className={STYLES.copyright}>
        <div className={STYLES.copyright__container}>
          <BpkText textStyle="sm" className={STYLES.copyright__content}>
            {fillPlaceholders(footerCopyright, { currentYear })}
          </BpkText>
        </div>
      </div>
    </StringsPropVerification>
  );
};

export default Copyright;
